import { Controller } from "@hotwired/stimulus";
import { Jodit } from "jodit";
import "jodit/esm/plugins/all.js";

const BASE64_IMG_PATTERN = /src="(data:image\/[^;]+;base64,[^"]+)"/g;

export default class extends Controller {
  connect() {
    // Prevent Turbo from intercepting form submissions inside Jodit dialogs/popups
    this.turboGuard = (e) => {
      if (e.target.closest(".jodit-dialog, .jodit-popup, .jodit-filebrowser")) {
        e.detail?.formSubmission?.stop();
        e.preventDefault();
      }
    };
    document.addEventListener("turbo:submit-start", this.turboGuard);

    this.editor = Jodit.make(this.element, {
      height: 400,
      toolbarButtonSize: "middle",
      buttons: [
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "subscript",
        "superscript",
        "|",
        "ul",
        "ol",
        "|",
        "outdent",
        "indent",
        "|",
        "font",
        "fontsize",
        "lineHeight",
        "brush",
        "paragraph",
        "|",
        "align",
        "|",
        "image",
        "file",
        "video",
        "table",
        "link",
        "symbols",
        "|",
        "hr",
        "eraser",
        "copyformat",
        "|",
        "cut",
        "copy",
        "paste",
        "selectall",
        "|",
        "undo",
        "redo",
        "|",
        "find",
        "spellcheck",
        "speechRecognize",
        "|",
        "source",
        "preview",
        "print",
        "fullsize",
        "about",
      ],
      // Base64 keeps images visible in the editor immediately.
      // They are uploaded and replaced with blob URLs before the form submits.
      uploader: { insertImageAsBase64URI: true },
      video: {
        parseUrlToVideoEmbed: (url, { width, height }) => {
          return `<iframe src="${url}" width="${width}" height="${height}" allowfullscreen="true" frameborder="0"></iframe>`;
        },
      },
      cleanHTML: { denyTags: "script,object,embed" },
      // Keep Turbo from stripping the textarea value on navigation
      events: {
        beforeDestruct: () => {
          if (this.editor) {
            this.element.value = this.editor.value;
          }
        },
      },
    });

    this.element.form?.addEventListener("submit", this.handleSubmit);
  }

  disconnect() {
    document.removeEventListener("turbo:submit-start", this.turboGuard);
    this.element.form?.removeEventListener("submit", this.handleSubmit);
    this.editor?.destruct();
    this.editor = null;
  }

  // Intercept submit: upload any base64 images first, then re-submit.
  // _submitReady flag prevents infinite loop when requestSubmit() re-fires the event.
  handleSubmit = async (event) => {
    if (this._submitReady) {
      this._submitReady = false;
      return;
    }

    event.preventDefault();

    this.syncValue();
    await this.uploadBase64Images();

    this._submitReady = true;
    event.target.requestSubmit();
  };

  syncValue = () => {
    if (this.editor) {
      this.element.value = this.editor.value;
    }
  };

  // Find every base64 <img> in the textarea, upload it, replace with the blob URL.
  async uploadBase64Images() {
    let html = this.element.value;
    const matches = [...html.matchAll(BASE64_IMG_PATTERN)];
    if (matches.length === 0) return;

    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");

    for (const match of matches) {
      try {
        const url = await this.uploadDataUri(match[1], csrfToken);
        html = html.replace(match[0], `src="${url}"`);
      } catch (e) {
        console.error("[Jodit] Image upload failed:", e.message);
      }
    }

    // Write back the updated HTML so the form submits clean URLs
    this.element.value = html;
    if (this.editor) this.editor.value = html;
  }

  async uploadDataUri(dataUri, csrfToken) {
    const [meta, base64] = dataUri.split(",");
    const mimeType = meta.match(/:(.*?);/)[1];
    const extension = mimeType.split("/")[1];

    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const file = new File([bytes], `editor_image.${extension}`, {
      type: mimeType,
    });

    const body = new FormData();
    body.append("files[]", file);

    const response = await fetch("/editor_uploads", {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken },
      body,
    });

    if (!response.ok) {
      throw new Error(`Server responded ${response.status}`);
    }

    const data = await response.json();
    if (!data.files?.[0]) throw new Error("No URL in response");

    return data.files[0];
  }
}
